/**
 * Text / Message API — ports AngularJS generalDataService text functions + textService
 *
 * Endpoints under /API/Text/ on spiglassapi.
 * searchMessages() is in messageDashboardApi.ts (already ported).
 */
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';

const API_BASE = `${buildApiBase()}/API/Text`;

// ── Types ────────────────────────────────────────────────────────────────────

export interface TextHeader {
  textHeaderId: number;
  propertyId: number;
  orderNumber: number | null;
  textType: string;
  subject: string;
  confidential: boolean;
  qc: boolean;
  subType: string | null;
  clientViewableText: string | null;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
}

export interface TextDetail {
  textDetailId: number;
  textHeaderId: number;
  text: string;
  createdBy: string;
  createdDate: string;
}

export interface UpdateTextRequest {
  textId: number | null;
  orderNumber: number;
  text: string;
  textType: string;
  propertyId: number;
  confidential: boolean;
  qc: boolean;
  subType: string | null;
  clientViewableText: string | null;
  subject?: string;
  cc?: string;
}

export interface SaveTextAssignmentRequest {
  textHeaderId: number;
  parentId: number | null;
  /** Recipient username. The backend expects `userName`, NOT `to` — sending
   *  `to` silently saves the message without a recipient, so it never shows
   *  up on anyone's Message Dashboard. */
  userName: string;
  type: string;
  status: number;
  comments: string;
}

// ── API Functions ────────────────────────────────────────────────────────────

/** GET /API/Text/ListTextByOrder — List texts for a specific order */
export async function listTextByOrder(
  orderNumber: number,
  propertyId: number
): Promise<TextHeader[]> {
  const response = await axiosClient.get<TextHeader[]>(`${API_BASE}/ListTextByOrder`, {
    params: { orderNumber, propertyId },
  });
  return response.data;
}

/** GET /API/Text/ListTexts — List all texts for a property */
export async function listTexts(propertyId: number): Promise<TextHeader[]> {
  const response = await axiosClient.get<TextHeader[]>(`${API_BASE}/ListTexts`, {
    params: { propertyId },
  });
  return response.data;
}

/** GET /API/Text/ListTextHeaders — List text headers for a property (optionally filtered by order) */
export async function listTextHeaders(
  propertyId: number,
  orderNumber?: number
): Promise<TextHeader[]> {
  const params: Record<string, unknown> = { propertyId };
  if (orderNumber != null) params.orderNumber = orderNumber;
  const response = await axiosClient.get<TextHeader[]>(`${API_BASE}/ListTextHeaders`, { params });
  return response.data;
}

/** GET /API/Text/GetTextDetails — Get full detail for a text header */
export async function getTextDetails(textHeaderId: number): Promise<TextDetail[]> {
  const response = await axiosClient.get<TextDetail[]>(`${API_BASE}/GetTextDetails`, {
    params: { textHeaderId },
  });
  return response.data;
}

/** PUT /API/Text/UpdateText — Create or update a text entry */
export async function updateText(request: UpdateTextRequest): Promise<unknown> {
  const response = await axiosClient.put(`${API_BASE}/UpdateText`, request);
  return response.data;
}

/**
 * PUT /API/Text/SendText — Send a text as email.
 *
 * Mirrors AngularJS generalDataService.sendEmail / propertyTextPanel.js sendEmail:
 *   - `to`  → semicolon-separated string split into an array of trimmed,
 *             non-empty addresses; backend expects a string[].
 *   - `cc`  → same transform, but an empty/missing value becomes `null` (not "").
 */
export async function sendText(
  textHeaderId: number,
  to: string,
  cc: string,
  subject: string
): Promise<unknown> {
  const toList = to
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const ccList = cc
    ? cc
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    : [];
  // Build the payload conditionally so `subject` is omitted from the body
  // when the caller left it blank — sending an empty / whitespace-only
  // string makes the backend overwrite the default subject with "".
  const trimmedSubject = (subject ?? '').trim();
  const payload: Record<string, unknown> = {
    textHeaderId,
    to: toList,
    cc: ccList.length > 0 ? ccList : null,
  };
  if (trimmedSubject) {
    payload.subject = trimmedSubject;
  }
  const response = await axiosClient.put(`${API_BASE}/SendText`, payload);
  return response.data;
}

/** PUT /API/Text/SaveTextAssignment — Save a message/assignment on a text */
export async function saveTextAssignment(request: SaveTextAssignmentRequest): Promise<unknown> {
  const response = await axiosClient.put(`${API_BASE}/SaveTextAssignment`, request);
  return response.data;
}

/**
 * PUT /API/Text/UpdateClientViewableText — Update client-viewable text flag.
 *
 * Backend expects a JSON body `{ textId: number, clientViewableText: boolean }`.
 * Sending these as query-string params returns 500 — ASP.NET model-binding
 * can't reconcile the absent body with the controller's expected model and
 * throws while binding.
 */
export async function updateClientViewableText(
  textId: number,
  clientViewableText: boolean
): Promise<unknown> {
  const response = await axiosClient.put(`${API_BASE}/UpdateClientViewableText`, {
    textId,
    clientViewableText,
  });
  return response.data;
}
